import { useSelector } from "react-redux";
import { useEffect, useMemo } from "react";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { pickBy } from "lodash";

// Subscriptions
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { onCreateChangeRequest, onUpdateChangeRequest } from "@/Washouts/api/PayrollClosing/subscriptions";
// Callbacks
import { createOrUpdateChangeRequestCallback } from "@/Washouts/helpers/ChangeRequest/subscriptionCallbacks";

// Types
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { RootState } from "@/redux/store";
import useAccessPositions from "../WashoutReview/useAccessPositions";
import { useCommissionSupportTeam } from "../useCommissionSupportTeam";
import { normalizePositions } from "@/Common/helpers/Positions";
import { useManagedPositions } from "@/Common/hooks/Positions";
import { errorLogger } from "@/Common/helpers/logger";

export type TChangeRequestCallback = (response: IGetChangeRequestsByCommissionCycle, queryClient: QueryClient, isCreate?: boolean) => void;

const onCreateCallback =
	(queryClient: QueryClient) =>
	(response: GraphqlQueryResponse<"onCreateChangeRequest", IGetChangeRequestsByCommissionCycle>["data"]) => {
		const data = response.data?.onCreateChangeRequest;
		if (!data?.requests?.length) {
			errorLogger("onCreateChangeRequest", { message: "No requests found" });
			return;
		}
		createOrUpdateChangeRequestCallback(data, queryClient, true);
	};

const onUpdateCallback =
	(queryClient: QueryClient) =>
	(response: GraphqlQueryResponse<"onUpdateChangeRequest", IGetChangeRequestsByCommissionCycle>["data"]) => {
		const data = response.data?.onUpdateChangeRequest;
		if (!data?.requests?.length) {
			errorLogger("onUpdateChangeRequest", { message: "No requests found" });
			return;
		}
		createOrUpdateChangeRequestCallback(data, queryClient);
	};

interface IUseChangeRequestSubscriptionsArgs {
	commissionCycleId?: string;
	payPeriodId?: string;
	userId?: string;
	position?: string;
	extraIdentifier?: string;
	enabled?: boolean;
}

export const useChangeRequestSubscriptions = (args: IUseChangeRequestSubscriptionsArgs) => {
	const locationId = useSelector((state: RootState) => state?.location?.location?.locationId);
	const {
		payPeriodId = "",
		commissionCycleId = "",
		userId = "",
		position: requestedPosition = "",
		extraIdentifier = "",
		enabled = true,
	} = args;
	const { addSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const currentUserId = useSelector((state: RootState) => state.user.user?._id);

	const { data: managedPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const positionAccess = useAccessPositions();

	const baseIdentifier = useMemo(
		() => ["changeRequests", commissionCycleId, payPeriodId, extraIdentifier],
		[commissionCycleId, payPeriodId, extraIdentifier]
	);

	const { userIsInSupportTeam, isLoading: loadingSupportTeam } = useCommissionSupportTeam();

	const addCallbacks = (identifierList: { identifier: QueryKey }[]) => {
		identifierList.forEach(({ identifier }) => {
			const foundSubscriptions = getSubscription(identifier);
			foundSubscriptions?.subscriptions?.onCreateChangeRequest.setCallback(onCreateCallback(queryClient));
			foundSubscriptions?.subscriptions?.onUpdateChangeRequest.setCallback(onUpdateCallback(queryClient));
		});
	};

	const addSubscriptions = (identifierList: { identifier: QueryKey; position: string }[]) => {
		identifierList.forEach(({ identifier, position }) => {
			const variables = pickBy(
				{
					locationId,
					payPeriodId,
					userId,
					position,
				},
				Boolean
			);
			addSubscription(
				identifier,
				[
					{
						context: "onCreateChangeRequest",
						query: onCreateChangeRequest,
						variables,
					},
					{
						context: "onUpdateChangeRequest",
						query: onUpdateChangeRequest,
						variables,
					},
				],
				false
			);
		});
	};

	const normalizedPosition = normalizePositions(requestedPosition);

	const positionsWithAccess =
		requestedPosition && positionAccess?.[normalizedPosition]?.edit
			? [requestedPosition]
			: managedPositions?.filter(
					(positionName) =>
						positionAccess[normalizePositions(positionName)]?.edit ||
						(positionName === requestedPosition && userId === currentUserId)
			  ) ?? [];

	const positionIdentifiers = positionsWithAccess.map((positionName) => ({
		identifier: [...baseIdentifier, positionName],
		position: positionName,
	}));

	const usedIdentifiers = userIsInSupportTeam ? [{ identifier: baseIdentifier, position: requestedPosition }] : positionIdentifiers;

	if (!loadingSupportTeam) {
		addCallbacks(usedIdentifiers);
	}

	useEffect(() => {
		if (!locationId || loadingSupportTeam || !enabled) {
			return;
		}
		addSubscriptions(usedIdentifiers);
	}, [locationId, loadingSupportTeam, enabled, payPeriodId, commissionCycleId, userId, requestedPosition, extraIdentifier]);
};
